/** TradeUnionProfile — public-facing ghost + active trade union page.
 *
 *  Route: `/trade-union/:slug`
 *
 *  Renders the full ghost profile: hero + tagline + about + fee table +
 *  office bearers + notices. Ghost profiles are watermarked "Unclaimed"
 *  and show a prominent "Claim this listing" CTA. Active profiles hide
 *  the ghost watermark and expose Apply / Message / Pay-Fee actions.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ShieldCheck, MapPin, Users, Calendar, Building2, Mail, Phone,
  ExternalLink, BadgeCheck, Info, ArrowRight, Award, Send,
  UserPlus, CreditCard, Lock, Sparkles,
} from "lucide-react";

import { getBackendUrl } from "../lib/backend-url";
const API = `${getBackendUrl()}/api`;

export default function TradeUnionProfile() {
  const { slug } = useParams();
  const [union, setUnion] = useState(null);
  const [ctx, setCtx]     = useState(null);
  const [err, setErr]     = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/trade-unions/${slug}`)
      .then(({ data }) => setUnion(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load"));
    // Best-effort — the endpoint tolerates anonymous callers.
    api
      .get(`/trade-unions/${slug}/viewer-context`)
      .then(({ data }) => setCtx(data))
      .catch(() => setCtx({ is_logged_in: false, is_member: false }));
  }, [slug]);

  if (err) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-24 text-center"
           data-testid="trade-union-error">
        <div className="text-white/60 text-sm">{err}</div>
        <Link to="/directory/orgs?type=trade_org"
              className="mt-4 inline-block text-[#F5B82E] text-xs uppercase tracking-[0.22em]">
          Back to directory
        </Link>
      </div>
    );
  }

  if (!union) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-24 text-white/40 text-sm">
        Loading…
      </div>
    );
  }

  const isGhost      = union.is_ghost && (!union.claim_status || union.claim_status === "unclaimed");
  const isOnboarding = union.claim_status &&
    ["claim_initiated", "claim_pending_kyc", "claim_pending_admin"].includes(union.claim_status);

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-10"
         data-testid="trade-union-profile-page">

      {/* Ghost banner — truly unclaimed, big amber CTA */}
      {isGhost && <GhostClaimBanner slug={slug} union={union} />}

      {/* Onboarding nudge — someone's mid-claim; visible to everyone
          except the union's own admin (they see the dashboard CTA below). */}
      {isOnboarding && !ctx?.is_union_admin && (
        <OnboardingClaimNudge slug={slug} union={union} />
      )}

      {/* Admin CTA — visible only when viewer administers this union */}
      {ctx?.is_union_admin && (
        <div className="mt-4 cs-card rounded-2xl p-4 border-emerald-500/40 bg-emerald-500/8 flex items-center justify-between gap-3 flex-wrap"
              data-testid="admin-dash-cta">
          <div className="text-[12px] text-white/85">
            You&apos;re signed in as an admin of this union.
          </div>
          <Link to={`/trade-union-dashboard/${slug}`}
                className="btn-themed h-9 px-4 rounded-full inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em]"
                data-testid="admin-dash-link">
            Open Dashboard <ArrowRight size={12}/>
          </Link>
        </div>
      )}

      {/* Hero */}
      <div className="mt-6 cs-card rounded-3xl overflow-hidden">
        {union.hero_image_url ? (
          <div
            className="h-40 sm:h-56 bg-cover bg-center relative"
            style={{ backgroundImage: `url(${union.hero_image_url})` }}
            data-testid="trade-union-hero">
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
          </div>
        ) : (
          <div className="h-32 sm:h-40 relative overflow-hidden"
               data-testid="trade-union-hero-placeholder">
            <div className="absolute inset-0 opacity-25"
                 style={{
                    background: `radial-gradient(circle at 30% 30%, ${accentFor(union.union_type)} 0%, transparent 55%),
                                 radial-gradient(circle at 80% 60%, ${accentFor(union.union_type)}80 0%, transparent 50%)`
                  }}/>
          </div>
        )}

        <div className="px-6 sm:px-8 pb-8 -mt-14 relative">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl border-4 border-black bg-white/6 backdrop-blur flex items-center justify-center overflow-hidden shrink-0"
                 data-testid="trade-union-logo">
              {union.logo_url ? (
                <img src={union.logo_url}
                     alt={`${union.brand_name} logo`}
                     className="w-full h-full object-contain p-2"/>
              ) : (
                <ShieldCheck size={40} className="text-white/40"/>
              )}
            </div>
            <div className="flex-1 min-w-0 pb-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight">
                  {union.brand_name}
                </h1>
                {union.claim_status === "active" && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-500/50 bg-emerald-500/10 text-emerald-300 text-[10px] tracking-[0.06em]"
                        data-testid="trade-union-verified-chip">
                    <BadgeCheck size={11}/> Verified · Active
                  </span>
                )}
                {isOnboarding && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-sky-500/50 bg-sky-500/10 text-sky-300 text-[10px] tracking-[0.16em] uppercase font-semibold"
                        data-testid="trade-union-onboarding-chip">
                    <Sparkles size={11}/> Onboarding
                  </span>
                )}
                {isGhost && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-500/50 bg-amber-500/10 text-amber-300 text-[10px] tracking-[0.16em] uppercase font-semibold"
                        data-testid="trade-union-unclaimed-chip">
                    Unclaimed
                  </span>
                )}
              </div>
              <div className="mt-1 text-white/60 text-sm truncate">
                {union.legal_name}
              </div>
              <div className="mt-2 flex items-center gap-3 flex-wrap text-[11px] text-white/55">
                <span className="inline-flex items-center gap-1"
                      data-testid="trade-union-archetype">
                  <Building2 size={11}/> {union.union_type_label}
                </span>
                {(union.registered_city || union.jurisdiction) && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin size={11}/> {union.registered_city ? `${union.registered_city} · ` : ""}
                    {union.jurisdiction}
                  </span>
                )}
                {union.establishment_year && (
                  <span className="inline-flex items-center gap-1">
                    <Calendar size={11}/> Est. {union.establishment_year}
                  </span>
                )}
                {union.member_count_approx && (
                  <span className="inline-flex items-center gap-1">
                    <Users size={11}/> ~{Number(union.member_count_approx).toLocaleString("en-IN")} members
                  </span>
                )}
              </div>
              {union.tagline && (
                <p className="mt-3 text-sm text-white/80 italic max-w-3xl">
                  {union.tagline}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* About */}
      {union.about_text && (
        <section className="mt-8 cs-card rounded-2xl p-6"
                 data-testid="trade-union-about">
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 mb-3">
            About
          </div>
          <p className="text-sm text-white/80 leading-relaxed whitespace-pre-line">
            {union.about_text}
          </p>
        </section>
      )}

      {/* Fee structure */}
      {union.member_categories?.length > 0 && (
        <section className="mt-8 cs-card rounded-2xl p-6"
                 data-testid="trade-union-fees">
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 mb-4">
            Membership Categories & Fees
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {union.member_categories.map((c, i) => (
              <div key={c.key || i}
                   className="rounded-xl border border-white/10 p-4"
                   data-testid={`trade-union-fee-${c.key}`}>
                <div className="font-display text-base uppercase tracking-tight">
                  {c.label}
                </div>
                {c.description && (
                  <p className="mt-1 text-[12px] text-white/60 leading-relaxed">
                    {c.description}
                  </p>
                )}
                <div className="mt-3 flex gap-4 flex-wrap text-[11px]">
                  {c.joining_fee_inr > 0 && (
                    <div>
                      <div className="text-white/40 text-[9px] uppercase tracking-[0.16em]">Joining</div>
                      <div className="text-white/90">₹{Number(c.joining_fee_inr).toLocaleString("en-IN")}</div>
                    </div>
                  )}
                  {c.annual_fee_inr > 0 && (
                    <div>
                      <div className="text-white/40 text-[9px] uppercase tracking-[0.16em]">Annual</div>
                      <div className="text-white/90">₹{Number(c.annual_fee_inr).toLocaleString("en-IN")}</div>
                    </div>
                  )}
                  {c.life_fee_inr && (
                    <div>
                      <div className="text-white/40 text-[9px] uppercase tracking-[0.16em]">Life</div>
                      <div className="text-white/90">₹{Number(c.life_fee_inr).toLocaleString("en-IN")}</div>
                    </div>
                  )}
                </div>
                {c.benefits?.length > 0 && (
                  <ul className="mt-3 space-y-1">
                    {c.benefits.map((b, k) => (
                      <li key={k} className="text-[11px] text-white/60 flex gap-1.5">
                        <span className="text-white/30">·</span>{b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
          {isGhost && (
            <div className="mt-4 text-[11px] text-amber-200/70 flex items-start gap-2">
              <Info size={12} className="mt-0.5 shrink-0"/>
              <span>
                Fees shown are pre-populated from the union&apos;s public
                website and may be out of date. The union will refresh
                these once they claim the profile.
              </span>
            </div>
          )}
        </section>
      )}

      {/* Office bearers */}
      {union.office_bearers?.length > 0 && (
        <section className="mt-8 cs-card rounded-2xl p-6"
                 data-testid="trade-union-bearers">
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 mb-4">
            Office Bearers
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {union.office_bearers.map((b, i) => (
              <div key={i}
                   className="rounded-xl border border-white/10 p-4 flex items-center gap-3"
                   data-testid={`trade-union-bearer-${i}`}>
                <div className="w-11 h-11 rounded-full bg-white/8 flex items-center justify-center overflow-hidden shrink-0">
                  {b.photo_url ? (
                    <img src={b.photo_url} alt={b.name} className="w-full h-full object-cover"/>
                  ) : (
                    <Users size={16} className="text-white/50"/>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-sm text-white/90 truncate">{b.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 truncate">
                    {b.role}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Affiliates — child craft unions under this umbrella */}
      <AffiliatesSection slug={slug} />

      {/* Apply · Message · Pay — the interaction primitives */}
      <MembershipApplySection union={union} ctx={ctx} slug={slug} />
      <MessageUnionSection    union={union} ctx={ctx} slug={slug} />
      <PayFeeSection          union={union} ctx={ctx} />

      {/* Contact */}
      <section className="mt-8 cs-card rounded-2xl p-6"
               data-testid="trade-union-contact">
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 mb-3">
          Contact
        </div>
        <div className="flex flex-wrap gap-4 text-sm text-white/75">
          {union.website_url && (
            <a href={union.website_url} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1.5 hover:text-white transition"
               data-testid="trade-union-website">
              <ExternalLink size={12}/> {union.website_url}
            </a>
          )}
          {union.contact_email && (
            <span className="inline-flex items-center gap-1.5">
              <Mail size={12}/> {union.contact_email}
            </span>
          )}
          {union.contact_phone && (
            <span className="inline-flex items-center gap-1.5">
              <Phone size={12}/> {union.contact_phone}
            </span>
          )}
        </div>
      </section>

      {/* Footer note */}
      <div className="mt-10 text-[11px] text-white/40 text-center">
        Trade-union profiles are curated by the CALL·SHEET team.
        Verified once claimed by an authorised office-bearer.
      </div>
    </div>
  );
}

/** The prominent amber banner that sits above ghost profiles inviting
 *  the union's own office-bearers to claim. */
function GhostClaimBanner({ slug, union }) {
  return (
    <div className="cs-card rounded-2xl p-5 border-amber-500/40 bg-amber-500/8 flex items-start gap-4"
         data-testid="trade-union-claim-banner">
      <Award size={20} className="text-amber-300 shrink-0 mt-0.5"/>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-[0.28em] uppercase text-amber-200 font-semibold">
          Unclaimed listing
        </div>
        <p className="mt-1 text-sm text-white/85 leading-relaxed">
          This profile of <span className="font-semibold">{union.brand_name}</span>{" "}
          has been curated by the CALL·SHEET team from public sources and is
          waiting to be claimed by an authorised office-bearer. Claiming
          activates your dashboard, unlocks membership fee collection, and
          verifies the profile with a ✓ badge.
        </p>
        <Link
          to={`/claim/${slug}`}
          className="mt-4 inline-flex items-center gap-2 h-9 px-4 rounded-full bg-amber-400 text-black text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-amber-300 transition"
          data-testid="trade-union-claim-btn">
          Claim this listing <ArrowRight size={13}/>
        </Link>
      </div>
    </div>
  );
}

/** Subtle sky-blue nudge for onboarding-state unions (someone has
 *  started the claim but hasn't crossed the KYC finish line yet).
 *  Shown to everyone except the union's own admin. Sits above the
 *  hero and gently pulls the actual office-bearer to finish. */
function OnboardingClaimNudge({ slug, union }) {
  const stageBlurb = {
    claim_initiated:
      "An office-bearer started the claim and is waiting on the verification OTP.",
    claim_pending_kyc:
      "Identity verified. KYC documents pending before this profile goes live.",
    claim_pending_admin:
      "KYC submitted. Awaiting a final Call·Sheet review before activation.",
  }[union.claim_status] || "Onboarding in progress.";

  return (
    <div className="cs-card rounded-2xl p-4 sm:p-5 border-sky-500/40 bg-sky-500/[0.06] flex items-start gap-3"
         data-testid="trade-union-onboarding-banner">
      <Sparkles size={18} className="text-sky-300 shrink-0 mt-0.5"/>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[10px] tracking-[0.28em] uppercase text-sky-200 font-semibold">
            Onboarding in progress
          </div>
          <span className="text-[10px] text-sky-200/60">·</span>
          <span className="text-[10px] text-sky-200/70">
            {stageBlurb}
          </span>
        </div>
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <p className="text-[12px] text-white/70 leading-relaxed flex-1 min-w-[240px]">
            Are you an authorised office-bearer of{" "}
            <span className="text-white/90 font-semibold">{union.brand_name}</span>?
            Take over from where the previous attempt paused and finish
            the KYC to activate the dashboard.
          </p>
          <Link
            to={`/claim/${slug}`}
            className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full border border-sky-400/60 bg-sky-500/15 text-sky-100 text-[10px] uppercase tracking-[0.22em] font-semibold hover:bg-sky-500/25 transition shrink-0"
            data-testid="trade-union-finish-claim-btn">
            Finish claim <ArrowRight size={11}/>
          </Link>
        </div>
      </div>
    </div>
  );
}

/** Colour accent per union archetype — used behind the hero when no
 *  hero_image_url is set. */
function accentFor(union_type) {
  return ({
    labor_union:         "#D9383A",
    professional_body:   "#6B4EFF",
    technical_body:      "#0EA5E9",
    collecting_society:  "#22D3EE",
    guild:               "#F5B82E",
  })[union_type] || "#F5B82E";
}


/** "Apply for Membership" — logged-in visitors pick a category and
 *  submit. Lands in the union's Applications inbox. */
function MembershipApplySection({ union, ctx, slug }) {
  const nav = useNavigate();
  const [cat, setCat]   = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy]   = useState(false);
  const [done, setDone]   = useState(false);

  const cats = union.member_categories || [];
  // If the union hasn't published categories yet, we still show the
  // section so applicants can "express interest" — the union will
  // categorise later.
  const noCats = cats.length === 0;

  const submit = async () => {
    if (!ctx?.is_logged_in) { nav("/login"); return; }
    if (!noCats && !cat) { toast.warning("Pick a membership category."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/trade-unions/${slug}/applications`, {
        category_key: cat || "express_interest",
        notes,
      });
      toast.success(`Application submitted — ref ${data.application_id.slice(0, 8)}`);
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit application.");
    } finally { setBusy(false); }
  };

  return (
    <section className="mt-8 cs-card rounded-2xl p-6"
              data-testid="trade-union-apply-section">
      <div className="flex items-center gap-2 mb-3">
        <UserPlus size={14} className="text-emerald-300"/>
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/60">
          Apply for Membership
        </div>
        {ctx?.is_member && (
          <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-500/50 bg-emerald-500/10 text-emerald-300 text-[9px] tracking-[0.16em] uppercase">
            <BadgeCheck size={9}/> You&apos;re a member
          </span>
        )}
      </div>

      {ctx?.is_member ? (
        <p className="text-sm text-white/60">
          You&apos;re already a verified member of {union.brand_name}. Head to
          your dashboard to manage your membership card &amp; dues.
        </p>
      ) : done ? (
        <p className="text-sm text-emerald-300" data-testid="apply-success">
          Application submitted — the union will review it and reach out.
          You&apos;ll see updates in your dashboard.
        </p>
      ) : !ctx?.is_logged_in ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-white/70">
            Log in to CALL·SHEET to apply — takes a minute.
          </p>
          <Link to="/login"
                className="btn-themed h-9 px-4 rounded-full inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em]"
                data-testid="apply-login-cta">
            <Lock size={12}/> Log in to apply
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {!noCats && (
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger data-testid="apply-category-trigger">
                <SelectValue placeholder="Pick a membership category" />
              </SelectTrigger>
              <SelectContent>
                {cats.map((c) => (
                  <SelectItem key={c.key} value={c.key}
                              data-testid={`apply-category-${c.key}`}>
                    {c.label}
                    {c.annual_fee_inr > 0 && ` · ₹${Number(c.annual_fee_inr).toLocaleString("en-IN")}/yr`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {noCats && (
            <div className="text-[12px] text-amber-200/70 flex items-start gap-2 md:col-span-2">
              <Info size={12} className="mt-0.5 shrink-0"/>
              <span>
                {union.brand_name} hasn&apos;t published membership categories on
                CALL·SHEET yet — submit an <em>expression of interest</em> and
                they&apos;ll route it once they claim their listing.
              </span>
            </div>
          )}
          <Textarea rows={2} value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional — briefly introduce yourself, mention past work, referrer name…"
                    data-testid="apply-notes"/>
          <div className="md:col-span-2">
            <Button onClick={submit} disabled={busy}
                    className="btn-themed h-10 px-5 rounded-full uppercase tracking-[0.22em] text-[11px]"
                    data-testid="apply-submit">
              {busy ? "Submitting…" : "Submit Application"}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}


/** "Message this Union" — signed-in members send directly (no
 *  re-login). Non-member visitors can also send but their pings are
 *  tagged non-member so the union can prioritise. */
function MessageUnionSection({ union, ctx, slug }) {
  const nav = useNavigate();
  const [subject, setSubject] = useState("");
  const [body, setBody]       = useState("");
  const [busy, setBusy]       = useState(false);
  const [sent, setSent]       = useState(false);

  const send = async () => {
    if (!ctx?.is_logged_in) { nav("/login"); return; }
    if (subject.trim().length < 2 || body.trim().length < 5) {
      toast.warning("Add a subject + a couple of lines.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/trade-unions/${slug}/messages`, {
        subject, body,
      });
      const label = data.is_from_member
        ? "Sent — the union sees this as a member message."
        : "Sent — the union sees this as a non-member enquiry.";
      toast.success(label);
      setSent(true);
      setSubject(""); setBody("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send.");
    } finally { setBusy(false); }
  };

  return (
    <section className="mt-8 cs-card rounded-2xl p-6"
              data-testid="trade-union-message-section">
      <div className="flex items-center gap-2 mb-3">
        <Send size={13} className="text-cyan-300"/>
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/60">
          Message {union.brand_name}
        </div>
        {ctx?.is_member && (
          <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-500/50 bg-emerald-500/10 text-emerald-300 text-[9px] tracking-[0.16em] uppercase"
                data-testid="message-member-badge">
            <BadgeCheck size={9}/> As a member
          </span>
        )}
      </div>

      {sent ? (
        <p className="text-sm text-emerald-300" data-testid="message-success">
          Landed in the union&apos;s inbox. Expect a reply on your registered
          email or via your CALL·SHEET dashboard.
        </p>
      ) : !ctx?.is_logged_in ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-white/70">
            Sign in first so the union knows who&apos;s writing.
          </p>
          <Link to="/login"
                className="btn-themed h-9 px-4 rounded-full inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em]"
                data-testid="message-login-cta">
            <Lock size={12}/> Log in to message
          </Link>
        </div>
      ) : (
        <div className="grid gap-3">
          <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                  placeholder="Subject — e.g. Membership renewal query"
                  data-testid="message-subject"/>
          <Textarea rows={4} value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="What would you like to ask the union?"
                    data-testid="message-body"/>
          <div>
            <Button onClick={send} disabled={busy}
                    className="btn-themed h-10 px-5 rounded-full uppercase tracking-[0.22em] text-[11px]"
                    data-testid="message-send">
              {busy ? "Sending…" : "Send Message"}
            </Button>
            {ctx?.is_member && (
              <span className="ml-3 text-[11px] text-white/50">
                Sending as <span className="text-white/80">{ctx.viewer_name || "member"}</span>
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}


/** "Pay Membership Fee" — placeholder until Sub-sprint D wires
 *  Razorpay Route. Once wired, this becomes the checkout link for the
 *  selected category. */
function PayFeeSection({ union, ctx }) {
  const hasFees = (union.member_categories || []).some(
    (c) => c.annual_fee_inr > 0 || c.joining_fee_inr > 0 || c.life_fee_inr,
  );
  if (!hasFees) return null;
  return (
    <section className="mt-8 cs-card rounded-2xl p-6"
              data-testid="trade-union-pay-section">
      <div className="flex items-center gap-2 mb-3">
        <CreditCard size={13} className="text-amber-300"/>
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/60">
          Pay Membership Fee
        </div>
        <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-500/50 bg-amber-500/10 text-amber-300 text-[9px] tracking-[0.16em] uppercase">
          Coming Soon
        </span>
      </div>
      <p className="text-sm text-white/60">
        We&apos;re wiring Razorpay Route so members can pay dues directly from
        this page — the union receives ~99% of the fee, the platform keeps
        a small fraction to cover the payment rails. Live in the next
        release.
      </p>
    </section>
  );
}


/* ────────────────────────────────────────────────────────────── */
/** Renders the list of child affiliates (e.g. FWICE's 36 craft unions).
 * Silently hides if this union has no affiliates. */
function AffiliatesSection({ slug }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/trade-unions/${slug}/affiliates`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, [slug]);
  if (!data || !data.count) return null;

  const byType = (data.affiliates || []).reduce((acc, a) => {
    const k = a.union_type || "other";
    (acc[k] = acc[k] || []).push(a);
    return acc;
  }, {});
  const TYPE_LABEL = {
    labor_union:        "Labor Unions",
    guild:              "Guilds",
    professional_body:  "Professional Bodies",
    technical_body:     "Technical Bodies",
    collecting_society: "Collecting Societies",
    other:              "Other",
  };

  return (
    <section className="mt-8 cs-card rounded-2xl p-6"
             data-testid="trade-union-affiliates">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <Users size={14} className="text-emerald-300"/>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/60">
            Affiliated Craft Unions · {data.count}
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-[0.2em] text-white/45">
          Each is its own registered body under this umbrella
        </span>
      </div>
      {Object.entries(byType).map(([type, rows]) => (
        <div key={type} className="mb-5 last:mb-0"
             data-testid={`trade-union-affiliates-group-${type}`}>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2">
            {TYPE_LABEL[type] || type} · {rows.length}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {rows.map((a) => (
              <Link key={a.slug}
                    to={`/trade-union/${a.slug}`}
                    className="rounded-lg bg-white/4 border border-white/8 p-3 hover:border-white/25 hover:bg-white/6 transition-colors block group"
                    data-testid={`trade-union-affiliate-${a.slug}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display text-sm uppercase tracking-tight truncate">
                      {a.brand_name}
                    </div>
                    <div className="text-[10px] text-white/55 truncate mt-0.5">
                      {a.legal_name}
                    </div>
                  </div>
                  {a.claim_status === "active" ? (
                    <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 text-[8px] tracking-[0.16em] uppercase shrink-0">Verified</span>
                  ) : a.claim_status && a.claim_status !== "unclaimed" ? (
                    <span className="px-1.5 py-0.5 rounded-full bg-sky-500/20 text-sky-200 text-[8px] tracking-[0.16em] uppercase shrink-0">Onboarding</span>
                  ) : (
                    <span className="px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-200 text-[8px] tracking-[0.16em] uppercase shrink-0">Unclaimed</span>
                  )}
                </div>
                {a.registered_city && (
                  <div className="mt-2 text-[10px] text-white/50 flex items-center gap-1">
                    <MapPin size={9}/> {a.registered_city}{a.registered_state ? `, ${a.registered_state}` : ""}
                  </div>
                )}
                <div className="mt-2 pt-2 border-t border-white/6 text-[10px] tracking-[0.2em] uppercase text-white/50 group-hover:text-white/85 flex items-center gap-1">
                  Open profile <ArrowRight size={10}/>
                </div>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}
